import { FeatureSource } from "../FeatureSource"
import { Feature, Point } from "geojson"
import { ImmutableStore, Store, UIEventSource } from "../../UIEventSource"
import { GeoOperations } from "../../GeoOperations"
import { Tiles } from "../../../Models/TileRange"
import { Lists } from "../../../Utils/Lists"

export interface ClusteringOptions {
    /**
     * If the zoomlevel is (strictly) above the specified value, don't cluster no matter how many features the tile contains.
     */
    dontClusterAboveZoom?: number
    /**
     * If the number of features in a _tile_ is equal or more then this number,
     * drop those features and emit a summary tile instead
     */
    cutoff?: 20 | number

    showSummaryAt?: "tilecenter" | "average"
}

interface SummaryProperties {
    id: string
    total: number
    tile_id: number
}

export class ClusteringFeatureSource<T extends Feature<Point> = Feature<Point>>
    implements FeatureSource<T>
{
    private readonly id: string
    private readonly showSummaryAt: "tilecenter" | "average"
    features: Store<T[]>

    /**
     *The clustering feature source works _only_ on points and is a preprocessing step for the ShowDataLayer.
     * If a tile contains many points, a 'summary' point is emitted instead in 'summaryPoints'.
     * The points from the summary will _not_ be emitted in 'this.features' in that case.
     *
     * We ignore the polygons, as polygons get smaller when zoomed out and thus don't clutter the map too much
     */
    constructor(
        upstream: FeatureSource<T>,
        currentZoomlevel: Store<number>,
        id: string,
        options?: ClusteringOptions
    ) {
        this.id = id
        this.showSummaryAt = options?.showSummaryAt ?? "average"
        const clusterCutoff = options?.dontClusterAboveZoom ?? 17
        const doCluster =
            options?.dontClusterAboveZoom === undefined
                ? new ImmutableStore(true)
                : currentZoomlevel.map((zoom) => zoom <= clusterCutoff)
        const cutoff = options?.cutoff ?? 20
        const summaryPoints = new UIEventSource<Feature<Point, SummaryProperties>[]>([])
        currentZoomlevel = currentZoomlevel.stabilized(500).map((z) => Math.floor(z))
        this.features = upstream.features.map(
            (features) => {
                if (!doCluster.data) {
                    summaryPoints.set([])
                    return features
                }

                const z = currentZoomlevel.data
                const perTile = GeoOperations.spreadIntoBboxes(features, z)
                const resultingFeatures = []
                const summary: Feature<Point, SummaryProperties>[] = []
                for (const tileIndex of perTile.keys()) {
                    const tileFeatures: Feature<Point>[] = perTile.get(tileIndex)
                    if (tileFeatures.length > cutoff) {
                        summary.push(this.createSummaryFeature(tileFeatures, tileIndex))
                    } else {
                        resultingFeatures.push(...tileFeatures)
                    }
                }
                summaryPoints.set(summary)
                return resultingFeatures
            },
            [doCluster, currentZoomlevel]
        )

        ClusterGrouping.singleton.registerSource(summaryPoints)
    }

    private createSummaryFeature(
        features: Feature<Point>[],
        tileId: number
    ): Feature<Point, SummaryProperties> {
        let coordinates: [number, number]
        if (this.showSummaryAt === "tilecenter") {
            const [z, x, y] = Tiles.tile_from_index(tileId)
            coordinates = Tiles.centerPointOf(z, x, y)
        } else {
            let lonSum = 0
            let latSum = 0
            for (const feature of features) {
                const [lon, lat] = feature.geometry.coordinates
                lonSum += lon
                latSum += lat
            }
            const lon = lonSum / features.length
            const lat = latSum / features.length
            coordinates = [lon, lat]
        }
        return {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates,
            },
            properties: {
                id: "summary_" + this.id + "_" + tileId,
                tile_id: tileId,
                total: features.length,
            },
        }
    }
}

/**
 * Groups multiple summaries together
 */
export class ClusterGrouping
    implements FeatureSource<Feature<Point, { total_metric: string; id: string }>>
{
    private readonly _features: UIEventSource<
        Feature<Point, { total_metric: string; id: string }>[]
    > = new UIEventSource([])
    public readonly features: Store<Feature<Point, { total_metric: string; id: string }>[]> =
        this._features

    public static readonly singleton = new ClusterGrouping()

    public readonly isDirty = new UIEventSource(false)

    private constructor() {
        this.isDirty.stabilized(200).addCallback((dirty) => {
            if (dirty) {
                this.update()
            }
        })
    }

    private allSource: Store<Feature<Point, { total: number; tile_id: number }>[]>[] = []

    private update() {
        const countPerTile = new Map<number, { lon: number; lat: number; count: number }[]>()
        for (const source of this.allSource) {
            for (const f of source.data) {
                const id = f.properties.tile_id
                if (!countPerTile.has(id)) {
                    countPerTile.set(id, [])
                }
                const ls = countPerTile.get(id)
                ls.push({
                    lon: f.geometry.coordinates[0],
                    lat: f.geometry.coordinates[1],
                    count: f.properties.total,
                })
            }
        }
        const features: Feature<Point, { total_metric: string; id: string }>[] = []
        const now = new Date().getTime() + ""
        for (const tileId of countPerTile.keys()) {
            const data = countPerTile.get(tileId)
            const total = Lists.sum(data.map((d) => d.count))
            const lon = Lists.sum(data.map((d) => d.lon * d.count)) / total
            const lat = Lists.sum(data.map((d) => d.lat * d.count)) / total

            features.push({
                type: "Feature",
                properties: {
                    total_metric: "" + total,
                    id: "clustered_all_" + tileId + "_" + now, // We add the date to force a fresh ID every time, this makes sure values are updated
                },
                geometry: {
                    type: "Point",
                    coordinates: [lon, lat],
                },
            })
        }
        this._features.set(features)
        this.isDirty.set(false)
    }

    public registerSource(features: Store<Feature<Point, SummaryProperties>[]>) {
        if (this.allSource.indexOf(features) >= 0) {
            console.error("This source has already been registered")
            return
        }
        this.allSource.push(features)
        features.addCallbackAndRun(() => {
            //this.isDirty.set(true)
            this.update()
        })
    }
}
